import React, { useState, useEffect } from "react";
import { useForm } from "../../hooks/useForm";
import FormInput from "./FormInput";
import { Button, MenuItem } from "@mui/material";
import { useCity } from "../../hooks/useCity";
import { useCountry } from "../../hooks/useCountry";
import { useDivision } from "../../hooks/useDivision";
import { useRole } from "../../hooks/useRole";
import { useAddUserWithUUID } from "../../hooks/useUser";
import { toast } from "react-toastify";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { IconButton, InputAdornment } from "@mui/material";
import { createUserWithEmailAndPassword } from "firebase/auth";
import { useTeam } from "../../hooks/useTeam";
import { adminAuth } from "../../config/firebaseAdminAuth";
import Select from "react-select";
import {
  SUPER_ADMIN,
  ADMIN,
  CSF,
  DIVISION_CONTROLLER,
  SALES_SUPERVISOR,
} from "../../constants/roles";

export const CreateUser = () => {
  const { data: divisionsData } = useDivision();
  const { data: countriesData } = useCountry();
  const { data: citiesData } = useCity();
  const { data: rolesData } = useRole();
  const { data: teamsData } = useTeam();

  const { mutate: addUserWithUUID } = useAddUserWithUUID();

  const [showPassword, setShowPassword] = useState(false);
  const [filteredTeams, setFilteredTeams] = useState([]);
  const [filteredCities, setFilteredCities] = useState([]);

  // React-select styling
  const selectStyles = {
    control: (provided, state) => ({
      ...provided,
      minHeight: '55px',
      borderColor: state.isFocused ? '#159bd7' : state.isHovered ? '#159bd7' : '#c4c4c4',
      boxShadow: state.isFocused ? '0 0 0 1px #159bd7' : 'none',
    }),
    placeholder: (provided) => ({
      ...provided,
      color: '#9e9e9e',
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? '#159bd7' : state.isFocused ? '#e3f2fd' : 'white',
      color: state.isSelected ? 'white' : '#333',
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 9999,
    }),
    menuPortal: (provided) => ({
      ...provided,
      zIndex: 9999,
    }),
  };
  
  // Transform countries data for react-select
  const countryOptions = countriesData?.map(country => ({
    value: country.id,
    label: country.name
  })) || [];

  // Transform cities data for react-select
  const cityOptions = filteredCities?.map(city => ({
    value: city.id,
    label: city.name
  })) || [];

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  // Handler for react-select country change
  const handleCountryChange = (selectedOption) => {
    setFieldValue("country", selectedOption ? selectedOption.value : "");
  };

  // Handler for react-select city change
  const handleCityChange = (selectedOption) => {
    setFieldValue("city", selectedOption ? selectedOption.value : "");
  };

  const initialValues = {
    firstName: "",
    familyName: "",
    email: "",
    password: "",
    upi: "",
    division: "",
    team: "",
    city: "",
    country: "",
    teamMembership: "",
    role: "",
    phoneNumber: "",
    specialty: "",
    medicalLicense: "",
    organization: "Abbott",
  };

  const validationRules = {
    firstName: { required: true, minLength: { value: 3 } },
    familyName: { required: true, minLength: { value: 3 } },
    email: { required: true },
    password: { required: true, minLength: { value: 8 } },
    upi: { required: true },
    division: { required: false },
    team: { required: false },
    city: { required: true },
    country: { required: true },
    role: { required: true },
  };

  const {
    values,
    errors,
    handleChange,
    handleBlur,
    validateAll,
    resetForm,
    setFieldValue,
  } = useForm(initialValues, validationRules);

  // Filter teams based on selected division
  useEffect(() => {
    if (values.division && teamsData) {
      const teams = teamsData.filter(
        (team) => team.division_id === values.division
      );
      setFilteredTeams(teams);
      // Reset team selection if the current team is not in the filtered list
      if (values.team && !teams.some((team) => team.id === values.team)) {
        setFieldValue("team", "");
      }
    } else {
      setFilteredTeams([]);
      setFieldValue("team", "");
    }
  }, [values.division, values.team, teamsData, setFieldValue]);

  // Filter cities based on selected country
  useEffect(() => {
    if (values.country && citiesData) {
      const cities = citiesData.filter(
        (city) => city.country_id === values.country
      );
      setFilteredCities(cities);
      // Reset city selection if the current city is not in the filtered list
      if (values.city && !cities.some((city) => city.id === values.city)) {
        setFieldValue("city", "");
      }
    } else {
      setFilteredCities([]);
      setFieldValue("city", "");
    }
  }, [values.country, values.city, citiesData, setFieldValue]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Run validation before attempting submission
    if (!validateAll()) {
      toast.error("Please fill all the required fields correctly!");
      return;
    }
    try {
      const userCredential = await createUserWithEmailAndPassword(
        adminAuth,
        values.email,
        values.password
      );
      const userId = userCredential.user.uid;
      const passwordData = userCredential.user.reloadUserInfo.passwordHash;
      // Prepare the user data for saving
      const userData = {
        first_name: values.firstName,
        family_name: values.familyName,
        email_address: values.email.toLowerCase(),
        password: passwordData,
        UPI_num: values.upi,
        division_id: values.division,
        team_id: values.team,
        city_id: values.city,
        country_id: values.country,
        role_id: values.role,
        phone_number: values.phoneNumber || "N/A",
        specialty: values.specialty || "N/A",
        medical_license: values.medicalLicense || "N/A",
        organization: values.organization,
        status: true,
      };

      addUserWithUUID({ userId, userData });
      toast.success("User Created Successfully!");

      resetForm();
    } catch (error) {
      console.error("Error creating user:", error);
      toast.error("Error creating user. Please try again.");
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-6">
        <FormInput
          label="First Name"
          name="firstName"
          value={values.firstName}
          onChange={handleChange}
          onBlur={handleBlur}
          error={errors.firstName}
        />
        <FormInput
          label="Family Name"
          name="familyName"
          value={values.familyName}
          onChange={handleChange}
          onBlur={handleBlur}
          error={errors.familyName}
        />
        <FormInput
          label="Email Address"
          type="email"
          name="email"
          value={values.email}
          onChange={handleChange}
          onBlur={handleBlur}
          error={errors.email}
        />
        <FormInput
          label="Password"
          name="password"
          value={values.password}
          onChange={handleChange}
          onBlur={handleBlur}
          error={errors.password}
          type={showPassword ? "text" : "password"}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={handleClickShowPassword} edge="end">
                  {showPassword ? (
                    <span className="text-blueColor">
                      <VisibilityIcon />
                    </span>
                  ) : (
                    <span className="text-blueColor">
                      <VisibilityOffIcon />
                    </span>
                  )}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <FormInput
          label="UPI Number"
          name="upi"
          value={values.upi}
          onChange={handleChange}
          onBlur={handleBlur}
          error={errors.upi}
        />
        <FormInput
          select
          label="Role"
          name="role"
          value={values.role}
          onChange={handleChange}
          onBlur={handleBlur}
          error={errors.role}
        >
          <MenuItem value="" disabled>
            Select a Role
          </MenuItem>
          {rolesData
            ?.filter((role) => role.name !== SUPER_ADMIN)
            .map((role) => (
              <MenuItem key={role.id} value={role.id}>
                {role.name}
              </MenuItem>
            ))}
        </FormInput>

        {values.role && rolesData && (
          <>
            {(![ADMIN, CSF, SUPER_ADMIN].includes(
              rolesData.find((r) => r.id === values.role)?.name
            ) ||
              rolesData.find((r) => r.id === values.role)?.name ===
              DIVISION_CONTROLLER) && (
                <FormInput
                  select
                  label="Division"
                  name="division"
                  value={values.division}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={errors.division}
                >
                  <MenuItem value="" disabled>
                    Select a Division
                  </MenuItem>
                  {divisionsData?.map((division) => (
                    <MenuItem key={division.id} value={division.id}>
                      {division.name}
                    </MenuItem>
                  ))}
                </FormInput>
              )}

            {![ADMIN, CSF, SUPER_ADMIN, DIVISION_CONTROLLER, SALES_SUPERVISOR].includes(
              rolesData.find((r) => r.id === values.role)?.name
            ) && (
                <FormInput
                  select
                  label="Team"
                  name="team"
                  value={values.team}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={errors.team}
                  disabled={!values.division}
                >
                  <MenuItem value="" disabled>
                    Select a Team
                  </MenuItem>
                  {filteredTeams?.map((team) => (
                    <MenuItem key={team.id} value={team.id}>
                      {team.name}
                    </MenuItem>
                  ))}
                </FormInput>
              )}
          </>
        )}

        <div className="flex flex-col">
          <label className="text-sm font-medium text-gray-700 mb-1">
            Country <span className="text-red-500">*</span>
          </label>
          <Select
            options={countryOptions}
            value={countryOptions.find(option => option.value === values.country) || null}
            onChange={handleCountryChange}
            placeholder="Select a Country"
            styles={selectStyles}
            isClearable
            isSearchable
            className="react-select-container"
            classNamePrefix="react-select"
          />
          {errors.country && (
            <span className="text-red-500 text-xs mt-1">{errors.country}</span>
          )}
        </div>
        <div className="flex flex-col">
          <label className="text-sm font-medium text-gray-700 mb-1">
            City <span className="text-red-500">*</span>
          </label>
          <Select
            options={cityOptions}
            value={cityOptions.find(option => option.value === values.city) || null}
            onChange={handleCityChange}
            placeholder="Select a City"
            styles={selectStyles}
            isClearable
            isSearchable
            isDisabled={!values.country}
            className="react-select-container"
            classNamePrefix="react-select"
          />
          {errors.city && (
            <span className="text-red-500 text-xs mt-1">{errors.city}</span>
          )}
        </div>

        <FormInput
          label="Phone Number"
          name="phoneNumber"
          value={values.phoneNumber}
          onChange={handleChange}
          onBlur={handleBlur}
        />
        <FormInput
          label="Specialty"
          name="specialty"
          value={values.specialty}
          onChange={handleChange}
          onBlur={handleBlur}
        />
        <FormInput
          label="Medical License"
          name="medicalLicense"
          value={values.medicalLicense}
          onChange={handleChange}
          onBlur={handleBlur}
        />
        <FormInput
          label="Organization"
          name="organization"
          value={values.organization}
          onChange={handleChange}
          onBlur={handleBlur}
        />
      </div>
      <div className="flex justify-center mt-6 pb-4">
        <Button
          data-track="create-user-button"
          data-module="Users"
          type="submit"
          variant="contained"
          style={{ backgroundColor: "#159bd7" }}
          className="px-6 py-2 w-60"
        >
          Create User
        </Button>
      </div>
    </form>
  );
};
